import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import priceTierService from "../../services/PriceTierService";

export default function PriceTierAddEditPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [products, setProducts] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [percentAdjust, setPercentAdjust] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (id) {
        // Edit mode
        const tiersRes = await priceTierService.getAll();
        const tier = (tiersRes.data?.data ?? tiersRes.data ?? []).find(
          (t) => t.id === parseInt(id)
        );
        if (tier) {
          setName(tier.name);
          setDescription(tier.description ?? "");
        }

        const prodRes = await priceTierService.getProducts(parseInt(id));
        setProducts(prodRes.data?.data ?? prodRes.data ?? []);
      } else {
        // Add mode
        setName("");
        setDescription("");
        const prodRes = await priceTierService.getProductsForNew();
        setProducts(prodRes.data?.data ?? prodRes.data ?? []);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Veriler yüklenirken bir hata oluştu!",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (productId, val) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.productId === productId) {
          return {
            ...p,
            priceInclVat: val,
          };
        }
        return p;
      })
    );
  };

  const copyDefaultPrices = () => {
    setProducts((prev) =>
      prev.map((p) => ({
        ...p,
        priceInclVat: p.defaultSalePrice,
      }))
    );
    toast.current?.show({
      severity: "info",
      summary: "Kopyalandı",
      detail: "Tüm ürünlerin standart satış fiyatları kopyalandı.",
      life: 2000,
    });
  };

  const applyPercentAdjust = () => {
    if (percentAdjust === null || percentAdjust === undefined) {
      toast.current?.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Lütfen geçerli bir yüzde oranı girin!",
      });
      return;
    }
    setProducts((prev) =>
      prev.map((p) => {
        const basePrice = p.defaultSalePrice || 0;
        const adjusted = basePrice * (1 + percentAdjust / 100);
        return {
          ...p,
          priceInclVat: Math.round(adjusted * 100) / 100,
        };
      })
    );
    toast.current?.show({
      severity: "info",
      summary: "Uygulandı",
      detail: `Tüm ürünlere standart fiyat üzerinden %${percentAdjust} değişim uygulandı.`,
      life: 2000,
    });
  };

  const clearPrices = () => {
    setProducts((prev) =>
      prev.map((p) => ({
        ...p,
        priceInclVat: null,
      }))
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Fiyat listesi adı boş olamaz!",
      });
      return;
    }

    try {
      setSaving(true);

      // Prepare payload prices list. Calculate PriceExclVat on client side for backend completeness
      const pricesPayload = products
        .filter((p) => p.priceInclVat !== null && p.priceInclVat !== undefined)
        .map((p) => {
          const incl = p.priceInclVat;
          const excl = incl / (1 + (p.taxRate || 0) / 100);
          return {
            productId: p.productId,
            priceInclVat: incl,
            priceExclVat: excl,
          };
        });

      let res;
      if (id) {
        // Update
        const payload = {
          id: parseInt(id),
          name: name.trim(),
          description: description.trim(),
          prices: pricesPayload,
        };
        res = await priceTierService.update(payload);
      } else {
        // Create
        const payload = {
          name: name.trim(),
          description: description.trim(),
          prices: pricesPayload,
        };
        res = await priceTierService.create(payload);
      }

      const isSuccess = res.data?.success || res.success;
      const message = res.data?.message || res.message;

      if (isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: id ? "Fiyat listesi güncellendi." : "Yeni fiyat listesi oluşturuldu.",
        });
        setTimeout(() => {
          navigate("/products/price-tiers");
        }, 1000);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Hata",
          detail: message || "İşlem gerçekleştirilemedi!",
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Kaydetme işlemi başarısız oldu!",
      });
    } finally {
      setSaving(false);
    }
  };

  const priceInputTemplate = (rowData) => {
    return (
      <InputNumber
        value={rowData.priceInclVat}
        onValueChange={(e) => handlePriceChange(rowData.productId, e.value)}
        mode="currency"
        currency="TRY"
        locale="tr-TR"
        placeholder="Standart Fiyat Kullan"
        className="w-full max-w-[200px]"
        inputClassName="rounded-lg py-1.5 px-3 text-right font-semibold"
      />
    );
  };

  const defaultPriceTemplate = (rowData) => {
    return (
      <span className="text-slate-500 font-medium">
        {rowData.defaultSalePrice?.toLocaleString("tr-TR", {
          minimumFractionDigits: 2,
        })}{" "}
        ₺
      </span>
    );
  };

  return (
    <div className="p-2 md:p-4 animate-fade-in bg-[#f8fafc] min-h-screen">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <form onSubmit={handleSave} className="flex flex-col gap-6 max-w-6xl mx-auto">
        {/* Top Header & Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 md:p-6 border border-slate-200 rounded-2xl shadow-sm">
          <div className="flex items-center gap-4">
            <Button
              icon="pi pi-arrow-left"
              className="p-button-rounded p-button-text p-button-secondary border-none"
              onClick={() => navigate("/products/price-tiers")}
              type="button"
            />
            <div>
              <h1 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-tight">
                {id ? "Fiyat Listesini Düzenle" : "Yeni Fiyat Listesi Ekle"}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[9px] font-black rounded-lg uppercase border border-indigo-100">
                  Fiyat Seviyesi
                </span>
                <span className="text-slate-400 text-xs font-medium">
                  • Liste ismini belirleyin ve ürün fiyatlarını girin
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              label="İptal"
              icon="pi pi-times"
              className="p-button-outlined p-button-secondary rounded-xl px-4 py-2 text-sm"
              onClick={() => navigate("/products/price-tiers")}
              type="button"
              disabled={saving}
            />
            <Button
              label="Kaydet"
              icon="pi pi-check"
              className="!bg-emerald-500 border-none hover:!bg-emerald-600 rounded-xl px-6 py-2 text-sm font-black text-white shadow-lg shadow-emerald-500/20"
              type="submit"
              loading={saving}
            />
          </div>
        </div>

        {/* General Info Card */}
        <div className="bg-white p-4 md:p-6 border border-slate-200 rounded-2xl shadow-sm flex flex-col md:flex-row gap-6">
          <div className="flex-1 flex flex-col gap-2">
            <label htmlFor="list-name" className="text-sm font-bold text-slate-700">
              Fiyat Listesi Adı *
            </label>
            <InputText
              id="list-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Toptan Satış Fiyatı, Yaz Kampanyası"
              required
              className="rounded-xl py-3 px-4 text-base border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <label htmlFor="list-desc" className="text-sm font-bold text-slate-700">
              Açıklama (İsteğe Bağlı)
            </label>
            <InputTextarea
              id="list-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Bu fiyat listesi hangi durumlar veya müşteriler için kullanılacak..."
              rows={1}
              autoResize
              className="rounded-xl py-3 px-4 text-base border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Products Price Matrix Table */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 md:p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex flex-col">
              <h2 className="text-lg font-black text-slate-900 uppercase">Ürün Fiyatları</h2>
              <span className="text-xs text-slate-400 font-medium">
                Bu listede geçerli olacak fiyatları girin. Boş bırakılan ürünlerde standart fiyat geçerli olacaktır.
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="p-input-icon-left w-full sm:w-64">
                <i className="pi pi-search text-slate-400" />
                <InputText
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Ürün veya Barkod Ara..."
                  className="w-full rounded-xl py-2 px-3 pl-10 border-slate-200 text-sm"
                />
              </span>

              <Button
                label="Standart Fiyatları Kopyala"
                icon="pi pi-copy"
                onClick={copyDefaultPrices}
                className="p-button-outlined p-button-info rounded-xl text-xs py-2 px-3 font-semibold"
                type="button"
              />

              <InputNumber
                value={percentAdjust}
                onValueChange={(e) => setPercentAdjust(e.value)}
                suffix="%"
                placeholder="Oran %"
                className="w-24"
                inputClassName="w-full rounded-xl py-2 px-3 text-center border-slate-200 text-xs font-bold"
              />
              <Button
                label="Standartı % Uygula"
                icon="pi pi-percentage"
                onClick={applyPercentAdjust}
                className="p-button-outlined p-button-secondary rounded-xl text-xs py-2 px-3 font-semibold"
                type="button"
              />

              <Button
                label="Temizle"
                icon="pi pi-trash"
                onClick={clearPrices}
                className="p-button-outlined p-button-danger rounded-xl text-xs py-2 px-3"
                type="button"
              />
            </div>
          </div>

          <div className="p-4">
            <DataTable
              value={products}
              paginator
              rows={25}
              loading={loading}
              emptyMessage="Ürün bulunamadı"
              className="p-datatable-sm"
              responsiveLayout="scroll"
              rowHover
              rowsPerPageOptions={[10, 25, 50, 100]}
              globalFilter={globalFilter}
              globalFilterFields={["productName", "barcode"]}
            >
              <Column
                field="productName"
                header="ÜRÜN ADI"
                sortable
                headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                className="py-3 px-4 font-bold text-slate-700"
              />
              <Column
                field="barcode"
                header="BARKOD"
                headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                className="py-3 px-4 text-slate-500 font-medium"
              />
              <Column
                field="defaultSalePrice"
                header="STANDART SATIŞ FİYATI"
                body={defaultPriceTemplate}
                sortable
                headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                className="py-3 px-4"
              />
              <Column
                header="FİYAT LİSTESİ FİYATI"
                body={priceInputTemplate}
                headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                className="py-3 px-4"
                style={{ width: "240px" }}
              />
            </DataTable>
          </div>
        </div>
      </form>
    </div>
  );
}
